// Scoring utility functions for psychological safety calculations
import { QUESTIONS, STAGES } from '../config/constants';

/**
 * Get normalized score for a single response (handles reverse-scored items)
 */
export const getNormalizedScore = (response, question) => {
  if (response === undefined || response === null) return null;
  return question.positive ? response : (6 - response);
};

/**
 * Calculate average score for a set of questions
 */
export const calculateScore = (responses, questionIds = null) => {
  const relevantQuestions = questionIds 
    ? QUESTIONS.filter(q => questionIds.includes(q.id))
    : QUESTIONS;
  
  let total = 0;
  let count = 0;
  
  relevantQuestions.forEach(q => {
    const normalized = getNormalizedScore(responses[q.id], q);
    if (normalized !== null) {
      total += normalized;
      count++;
    }
  });
  
  return count > 0 ? total / count : 0;
};

/**
 * Calculate scores for all four stages
 */
export const calculateStageScores = (responses) => {
  const stages = {};
  
  Object.keys(STAGES).forEach(stage => {
    const stageQuestions = QUESTIONS.filter(q => q.stage === stage).map(q => q.id);
    stages[stage] = calculateScore(responses, stageQuestions);
  });
  
  return stages;
};

/**
 * Calculate overall psychological safety score
 */
export const calculateOverallScore = (responses) => {
  const stageScores = calculateStageScores(responses);
  const values = Object.values(stageScores);
  return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
};

/**
 * Calculate team averages from multiple participants
 */
export const calculateTeamAverages = (participants) => {
  if (!participants || participants.length === 0) return {};
  
  const completedParticipants = participants.filter(p => p.status === 'completed');
  if (completedParticipants.length === 0) return {};
  
  const averages = {};
  
  Object.keys(STAGES).forEach(stage => {
    const scores = completedParticipants.map(p => calculateStageScores(p.responses)[stage]);
    averages[stage] = scores.reduce((a, b) => a + b, 0) / scores.length;
  });
  
  return averages;
};

/**
 * Calculate range (min, max, spread) for a stage across participants
 */
export const calculateStageRange = (participants, stage) => {
  const completedParticipants = participants.filter(p => p.status === 'completed');
  if (completedParticipants.length === 0) {
    return { min: 0, max: 0, spread: 0 };
  }
  
  const scores = completedParticipants.map(p => calculateStageScores(p.responses)[stage]);
  
  return {
    min: Math.min(...scores),
    max: Math.max(...scores),
    spread: Math.max(...scores) - Math.min(...scores)
  };
};

/**
 * Calculate statistics for a single question across participants
 */
export const calculateQuestionStats = (participants, questionId) => {
  const question = QUESTIONS.find(q => q.id === questionId);
  const completedParticipants = participants.filter(p => p.status === 'completed' && p.responses[questionId] !== undefined);
  
  if (completedParticipants.length === 0) {
    return {
      average: 0,
      min: 0,
      max: 0,
      distribution: [0, 0, 0, 0, 0]
    };
  }
  
  const normalizedScores = completedParticipants.map(p => getNormalizedScore(p.responses[questionId], question));
  const rawScores = completedParticipants.map(p => p.responses[questionId]);
  
  return {
    average: normalizedScores.reduce((a, b) => a + b, 0) / normalizedScores.length,
    min: Math.min(...normalizedScores),
    max: Math.max(...normalizedScores),
    rawMin: Math.min(...rawScores),
    rawMax: Math.max(...rawScores),
    distribution: [1, 2, 3, 4, 5].map(v => rawScores.filter(s => s === v).length)
  };
};

/**
 * Identify development areas for an individual (below team average)
 */
export const identifyDevelopmentAreas = (participantResponses, teamAverages, threshold = 0.3) => {
  const participantScores = calculateStageScores(participantResponses);
  
  return Object.entries(participantScores)
    .filter(([stage, score]) => score < (teamAverages[stage] || 0) - threshold)
    .sort(([, a], [, b]) => a - b)
    .map(([stage]) => stage);
};

/**
 * Identify strength areas for an individual (above team average)
 */
export const identifyStrengthAreas = (participantResponses, teamAverages, threshold = 0.3) => {
  const participantScores = calculateStageScores(participantResponses);
  
  return Object.entries(participantScores)
    .filter(([stage, score]) => score >= (teamAverages[stage] || 0) + threshold)
    .sort(([, a], [, b]) => b - a)
    .map(([stage]) => stage);
};

/**
 * Get priority stage for team (lowest scoring)
 */
export const getPriorityStage = (teamAverages) => {
  const entries = Object.entries(teamAverages);
  if (entries.length === 0) return null;
  
  return entries.sort(([, a], [, b]) => a - b)[0][0];
};

/**
 * Calculate completion progress for a participant
 */
export const calculateProgress = (responses) => {
  const answeredCount = Object.keys(responses || {}).length;
  return {
    answered: answeredCount,
    total: QUESTIONS.length,
    percentage: (answeredCount / QUESTIONS.length) * 100
  };
};
